import React, { useEffect, useRef, useState, useCallback } from 'react';
import crmClient from './crmClient';
import { formatDateTime } from './format';

const STATUS_TABS = [
  { key: 'active', label: 'Active' },
  { key: 'closed', label: 'Closed' },
  { key: 'all', label: 'All' }
];

const LiveChatView = ({ channel, title }) => {
  const isSms = channel === 'sms';
  const isVoice = channel === 'voice';
  const [statusTab, setStatusTab] = useState(channel === 'voice' ? 'all' : 'active');
  const [conversations, setConversations] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const bodyRef = useRef(null);
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;
  const isPinnedToBottomRef = useRef(true);
  const lastMessageSignatureRef = useRef('');

  const loadList = useCallback(() => {
    crmClient
      .listConversations(statusTab, undefined, channel)
      .then(payload => setConversations((payload && payload.conversations) || []))
      .catch(() => {});
  }, [statusTab, channel]);

  const loadDetail = useCallback(id => {
    if (!id) return;
    setLoading(true);
    crmClient
      .getConversation(id)
      .then(payload => {
        setLoading(false);
        setDetail((payload && payload.conversation) || null);
        setMessages((payload && payload.messages) || []);
      })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  // Poll the list and the open thread for new messages.
  useEffect(() => {
    const timer = setInterval(() => {
      loadList();
      if (selectedRef.current) {
        crmClient
          .getConversation(selectedRef.current)
          .then(payload => {
            setMessages((payload && payload.messages) || []);
            setDetail((payload && payload.conversation) || null);
          })
          .catch(() => {});
      }
    }, 4000);
    return () => clearInterval(timer);
  }, [loadList]);

  useEffect(() => {
    if (selectedId) {
      // Opening a thread should land at the latest message.
      isPinnedToBottomRef.current = true;
      lastMessageSignatureRef.current = '';
      loadDetail(selectedId);
    }
  }, [selectedId, loadDetail]);

  // Only auto-scroll to the bottom when a new message actually arrives AND the user is pinned to the
  // bottom — otherwise the 4s poll (which replaces the messages array each tick) yanks the view down
  // while the user is scrolled up reading history.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const signature = `${messages.length}|${messages.length ? (messages[messages.length - 1].crm_message_id || '') : ''}`;
    const hasNewMessages = signature !== lastMessageSignatureRef.current;
    const isFirstRender = lastMessageSignatureRef.current === '';
    lastMessageSignatureRef.current = signature;
    if ((isFirstRender || hasNewMessages) && isPinnedToBottomRef.current) {
      body.scrollTop = body.scrollHeight;
    }
  }, [messages]);

  const handleBodyScroll = () => {
    const body = bodyRef.current;
    if (!body) return;
    const distanceFromBottom = body.scrollHeight - body.scrollTop - body.clientHeight;
    isPinnedToBottomRef.current = distanceFromBottom <= 40;
  };

  const send = () => {
    const text = draft.trim();
    if (!text || !selectedId) return;
    setSending(true);
    setMessages(prev => [...prev, { crm_message_id: `tmp-${Date.now()}`, sender: 'agent', body: text, created_at: new Date().toISOString() }]);
    setDraft('');
    crmClient
      .sendChatMessage(selectedId, text)
      .then(() => {
        setSending(false);
        loadDetail(selectedId);
        loadList();
      })
      .catch(() => setSending(false));
  };

  const closeChat = () => {
    if (!selectedId) return;
    crmClient.closeConversation(selectedId).then(() => {
      loadDetail(selectedId);
      loadList();
    });
  };

  const reopenChat = () => {
    if (!selectedId) return;
    crmClient.reopenConversation(selectedId).then(() => {
      loadDetail(selectedId);
      loadList();
    });
  };

  const visitorLabel = c =>
    ((isSms || isVoice) ? c.visitor_name || c.visitor_phone : c.visitor_name || c.visitor_email) ||
    c.visitor_phone ||
    c.visitor_email ||
    (isVoice ? 'Caller' : isSms ? 'SMS contact' : 'Website visitor');

  return (
    <div className="crm-livechat">
      <div className="crm-livechat-list">
        <div className="crm-livechat-tabs">
          {STATUS_TABS.map(t => (
            <button key={t.key} type="button" className={statusTab === t.key ? 'active' : ''} onClick={() => setStatusTab(t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        {conversations.length === 0 && <div className="crm-livechat-empty">No conversations.</div>}
        {conversations.map(c => (
          <button
            key={c.crm_conversation_id}
            type="button"
            className={`crm-livechat-item ${selectedId === c.crm_conversation_id ? 'active' : ''}`}
            onClick={() => setSelectedId(c.crm_conversation_id)}
          >
            <div className="crm-livechat-item-top">
              <strong>{visitorLabel(c)}</strong>
              {c.needs_agent && <span className="crm-livechat-badge">needs agent</span>}
              {!c.needs_agent && c.unread_agent_count > 0 && <span className="crm-livechat-badge crm-livechat-badge--muted">{c.unread_agent_count}</span>}
            </div>
            <div className="crm-livechat-preview">{c.preview ? `${c.preview.sender === 'visitor' ? '' : c.preview.sender === 'agent' ? 'You: ' : 'Bot: '}${c.preview.body}` : '—'}</div>
            <div className="crm-livechat-meta">{formatDateTime(c.last_message_at)}</div>
          </button>
        ))}
      </div>

      <div className="crm-livechat-thread">
        {!selectedId && <div className="crm-livechat-placeholder">{isVoice ? 'Select a call to view the transcript.' : 'Select a conversation to reply.'}</div>}
        {selectedId && detail && (
          <>
            <div className="crm-livechat-thread-head">
              <div>
                <strong>{visitorLabel(detail)}</strong>
                <div className="crm-muted">
                  {isVoice
                    ? `${detail.visitor_phone || 'unknown caller'}${detail.page_url ? ` · called ${detail.page_url}` : ''}`
                    : `${detail.visitor_email || 'no email'}${detail.visitor_phone ? ` · ${detail.visitor_phone}` : ''}${detail.page_url ? ` · from ${detail.page_url}` : ''}`}
                </div>
                {isVoice && detail.subject ? <div className="crm-muted">{detail.subject}</div> : null}
              </div>
              <div className="crm-livechat-actions">
                <span className={`crm-stage-pill crm-stage-pill--${detail.status === 'closed' ? 'slate' : detail.needs_agent ? 'amber' : 'green'}`}>{detail.status}</span>
                {detail.status === 'closed' ? (
                  <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={reopenChat}>Reopen</button>
                ) : (
                  <button type="button" className="crm-btn crm-btn--ghost crm-btn--sm" onClick={closeChat}>Close</button>
                )}
              </div>
            </div>

            <div className="crm-livechat-body" ref={bodyRef} onScroll={handleBodyScroll}>
              {loading && messages.length === 0 && <div className="crm-muted">Loading…</div>}
              {messages.map(m => (
                <div key={m.crm_message_id} className={`crm-chat-row crm-chat-row--${m.sender === 'agent' ? 'agent' : m.sender === 'visitor' ? 'visitor' : 'bot'}`}>
                  <div className="crm-chat-bubble">
                    {m.sender === 'bot' && <span className="crm-chat-sender">Bot</span>}
                    {m.sender === 'agent' && m.agent_name && <span className="crm-chat-sender">{m.agent_name}</span>}
                    <span className="crm-chat-text">{m.body}</span>
                    <span className="crm-chat-time">{formatDateTime(m.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>

            {detail.status !== 'closed' && (
              <div className="crm-livechat-composer">
                <textarea
                  rows={2}
                  value={draft}
                  placeholder={isSms || isVoice ? 'Type your text reply…' : 'Type your reply…'}
                  onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                />
                <button type="button" className="crm-btn" disabled={sending || !draft.trim()} onClick={send}>
                  Send
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LiveChatView;
